import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { routes } from 'routes';
import { Link } from 'react-router-dom';
import { IconWarningType, IconInfo, IconTrend, IconLimits } from 'svgIcons/MotionPortalIcons';
import './style.scss';
import { LEFT_PANEL_WIDTH_PERCENT, toggleOptions, scrollIntoViewOptions } from '../constants';
import colors from 'theme/_colors.scss';
import useUserCapability from 'sharedHooks/useUserCapability';
import { apiCallStatus, onlineHelpLinks } from 'helpers/constants';
import Tippy from '@tippy.js/react';
import { hideAll } from 'tippy.js';
import { getEnvironmentStageSelector } from 'helpers/selectors';
import { useSelector } from 'react-redux';
import IconExpandAll from 'svgIcons/MotionPortalIcons/IconExpandAll';
import OrganizationNode from './OrganizationNode';
import { HierarchyExpansionContext } from './HierarchyExpansionContext';
import { collectExpandableNodeIds, findAncestorNodeIds, collectSelectionNodeIds } from './hierarchyHelpers';

// Helper function to update overrides map with ancestor node IDs
const updateOverridesWithAncestors = (prev, ancestorIds) => {
    const next = new Map(prev);
    ancestorIds?.forEach(id => next.set(id, true));
    return next;
};

const HierarchyViewComponent = React.memo(({
    selectedAsset,
    onSelectAsset,
    leftPanelSize,
    allAssets,
    hierarchyData,
    conditionIndicesView,
    showFilter,
    hierarchyLoadingStatus,
    expandCollapseCommand,
    selectedAssetIds,
    selectedAssetSignalCounts
}) => {
    const { t: translate } = useTranslation();
    const headerRef = useRef(null);
    const [isHeaderLabelVisible, setIsHeaderLabelVisible] = useState(true);
    const userCapabilities = useUserCapability();
    const { i18n } = useTranslation();
    const environment = useSelector(getEnvironmentStageSelector);
    const languageCode = i18n.language;

    // Centralized expand/collapse state: expandMode is the tree-wide default, overrides holds manually-toggled nodes.
    const [expandMode, setExpandMode] = useState(toggleOptions.COLLAPSE);
    const [overrides, setOverrides] = useState(new Map());

    const isNodeExpanded = useCallback(
        (nodeId) => overrides.has(nodeId) ? overrides.get(nodeId) : expandMode === toggleOptions.EXPAND,
        [overrides, expandMode]
    );

    const toggleNode = useCallback((nodeId) => {
        setOverrides(prev => {
            const next = new Map(prev);
            const current = next.has(nodeId) ? next.get(nodeId) : expandMode === toggleOptions.EXPAND;
            next.set(nodeId, !current);
            return next;
        });
    }, [expandMode]);

    const expansionContextValue = useMemo(
        () => ({ isNodeExpanded, toggleNode }),
        [isNodeExpanded, toggleNode]
    );

    // Node ids (asset leaf + ancestors) with a selected signal, for the Cross Asset Analysis V2 blue-dot (empty/no-op elsewhere).
    const selectionNodeIds = useMemo(
        () => collectSelectionNodeIds(hierarchyData, selectedAssetIds, conditionIndicesView),
        [hierarchyData, selectedAssetIds, conditionIndicesView]
    );

    const expandableNodeIds = useMemo(
        () => collectExpandableNodeIds(hierarchyData, conditionIndicesView),
        [hierarchyData, conditionIndicesView]
    );

    const isEverythingExpanded = useMemo(
        () => expandableNodeIds.length > 0 && expandableNodeIds.every(isNodeExpanded),
        [expandableNodeIds, isNodeExpanded]
    );

    const handleToggleExpandAll = useCallback(() => {
        setExpandMode(isEverythingExpanded ? toggleOptions.COLLAPSE : toggleOptions.EXPAND);
        setOverrides(new Map());
    }, [isEverythingExpanded]);

    // Optional external control used by CrossAssetAnalysisV2 bottom controls.
    useEffect(() => {
        if (!expandCollapseCommand?.mode) { return; }
        if (![toggleOptions.EXPAND, toggleOptions.COLLAPSE].includes(expandCollapseCommand.mode)) { return; }
        setExpandMode(expandCollapseCommand.mode);
        setOverrides(new Map());
    }, [expandCollapseCommand?.nonce]);

    // Auto-expand ancestors for the selected asset; no startTransition here to avoid flashing the loading overlay on every click.
    useEffect(() => {
        if (!selectedAsset || !hierarchyData) { return; }
        const ancestorIds = findAncestorNodeIds(hierarchyData, selectedAsset, conditionIndicesView);
        if (ancestorIds?.length === 0) { return; }
        setOverrides(prev => updateOverridesWithAncestors(prev, ancestorIds));
    }, [selectedAsset?.assetId, hierarchyData]);

    useEffect(() => {
        if (leftPanelSize > 50) {
            setIsHeaderLabelVisible(true);
        } else {
            setIsHeaderLabelVisible(false);
        }
    }, [leftPanelSize]);

    const handleSelectAsset = useCallback(
        (asset) => { onSelectAsset && onSelectAsset(asset); },
        [onSelectAsset]
    );

    const hierarchyViewRef = useRef(null);

    const handleHierarchyScroll = useCallback(() => {
        hideAll({ duration: 0 });
    }, []);

    // Scroll the selected asset row into view after auto-expansion renders
    useEffect(() => {
        if (!selectedAsset?.assetId || !hierarchyData) { return; }
        const timer = setTimeout(() => {
            if (!hierarchyViewRef.current) { return; }
            const selectedRow = hierarchyViewRef.current.querySelector('.item.active');
            if (selectedRow) {
                selectedRow.scrollIntoView(scrollIntoViewOptions.SELECTED_ASSET);
            }
        }, 200);
        return () => clearTimeout(timer);
    }, [selectedAsset?.assetId, hierarchyData]);

    const InfoTooltip = ({ title, isLink, children, placement = 'bottom' }) => {
        return <div className='info-button-with-online-help' role='tooltip'>
            <Tippy
                content={
                    <>
                        <div className='info-tooltip__title'>
                            {title}
                        </div>
                        <div className='info-tooltip__body'>
                            {children}
                        </div>
                        {isLink &&
                            <a className='info-tooltip__link' rel='noreferrer' target='_blank' href={`${onlineHelpLinks[environment]}${languageCode}`} role='link' >
                                {translate('ABB.Powertrain.Frontend.assetOverviewTooltipLinkText')}
                            </a>
                        }
                    </>
                }
                className='asset-overview-tooltip'
                interactive={true}
                interactiveBorder={10}
                animation='scale'
                theme='light-border'
                trigger='click'
                placement={placement}
                maxWidth='400px'
                boundary='viewport'
                appendTo={document.body}
            >
                <button type='button' className='info-icon-button' aria-label='Info' onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                    <IconInfo width={'16'} height={'16'} color={'black'} />
                </button>
            </Tippy>
        </div>;
    };

    const renderHeader = () => {
        const isTitleVisible = leftPanelSize >= LEFT_PANEL_WIDTH_PERCENT;
        const isLeftpanelSizeSmall = leftPanelSize <= 50;
        const expandCollapseLabel = translate(
            isEverythingExpanded ? 'ABB.Powertrain.Frontend.collapseAll' : 'ABB.Powertrain.Frontend.expandAll'
        );
        return <div className='asset-list-header'>
            <div
                className={'header-item header-item--cell-fixed'}
                style={selectedAsset?.assetName ? { minWidth: '100px' } : {}}
            >
                <Tippy
                    content={expandCollapseLabel}
                    className={'hierarchy-expand-all-tooltip'}
                    interactive={true}
                    interactiveBorder={10}
                    animation='scale'
                    theme='light-border'
                    trigger='mouseenter'
                    placement='top'
                    maxWidth={300}
                >
                    <button
                        type='button'
                        className='hierarchy-expand-icon'
                        aria-label={expandCollapseLabel}
                        onClick={handleToggleExpandAll}
                    >
                        <IconExpandAll />
                    </button>
                </Tippy>
                {translate('ABB.Powertrain.Frontend.conditionIndicesOverviewNameColumnTitle')}
            </div>
            {
                isTitleVisible &&
                <div className={`header-item ${isLeftpanelSizeSmall ? 'left-sidebar-header-cell-open' : 'left-sidebar-header-cell-closed'}`} ref={headerRef}>
                    <div className='column-icon'>
                        <IconWarningType color={colors.black} />
                    </div>
                    {isHeaderLabelVisible && translate('ABB.Powertrain.Frontend.conditionIndicesOverviewThresholdColumnTitle')}
                    <InfoTooltip
                        title={`${translate('ABB.Powertrain.Frontend.assetOverviewConditionIndicesText')} - ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewThresholdColumnTitle')}`}
                        isLink={true}
                    >
                        <div className='threshold-content'>
                            <p className='threshold-content__para'>{translate('ABB.Powertrain.Frontend.assetOverviewThresholdFirstParagraph')}</p>
                        </div>
                    </InfoTooltip>
                </div>
            }
            {
                isTitleVisible &&
                <div className={`header-item ${isLeftpanelSizeSmall ? 'left-sidebar-header-cell-open' : 'left-sidebar-header-cell-closed'}`}>
                    <div className='column-icon'>
                        <IconTrend />
                    </div>
                    {userCapabilities?.hasMotorAnomalyDetection() ?
                        <>
                            {isHeaderLabelVisible &&
                                `${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnTitle')} & ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAnomalyColumnTitle')}`
                            }
                            <InfoTooltip
                                title={`${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnTitle')} & ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAnomalyColumnTitle')}`}
                                isLink={true}
                            >
                                <div className='trend-content'>
                                    <p>{translate('ABB.Powertrain.Frontend.conditionIndicesOverviewColumnInfoText')}</p>
                                    <div>
                                        <p><span className='title'>{translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnTitle')}</span> {translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnInfoText')}</p>
                                        <p><span className='title'>{translate('ABB.Powertrain.Frontend.anomalyDetectionTab')}</span> {translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAnomalyColumnInfoText')}</p>
                                    </div>
                                    <p>{translate('ABB.Powertrain.Frontend.conditionIndicesOverviewSeverityPerspectiveText')}</p>
                                </div>
                            </InfoTooltip>
                        </> : <>
                            {isHeaderLabelVisible && translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnTitle')}
                            <InfoTooltip
                                title={`${translate('ABB.Powertrain.Frontend.assetOverviewConditionIndicesText')} - ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnTitle')}`}
                                isLink={true}
                            >
                                <div className='trend-content'>
                                    <p className='trend-content__para'>{translate('ABB.Powertrain.Frontend.assetOverviewTrendFirstParagraph')}</p>
                                </div>
                            </InfoTooltip>
                        </>
                    }
                </div>
            }
            {
                isTitleVisible && <div className={`header-item ${isLeftpanelSizeSmall ? 'left-sidebar-header-cell-open' : 'left-sidebar-header-cell-closed'}`}>
                    <IconLimits />
                    {isHeaderLabelVisible && translate('ABB.Powertrain.Frontend.limitsTitle')}
                    <InfoTooltip
                        title={translate('ABB.Powertrain.Frontend.assetOverviewLimitsText')}
                        isLink={false}
                    >
                        <div className='limit-content'>
                            <p className='limit-content__para'>{translate('ABB.Powertrain.Frontend.assetOverviewLimitFirstParagraph')}</p>
                            <p className='limit-content__para'>
                                <b>{translate('ABB.Powertrain.Frontend.assetOverviewLimitPointTitle')}</b>
                                <span>{translate('ABB.Powertrain.Frontend.assetOverviewLimitPointContent')}</span>
                            </p>
                        </div>
                    </InfoTooltip>
                </div>
            }
        </div >;
    };
    if ([apiCallStatus.SUCCESS, apiCallStatus.ERROR].includes(hierarchyLoadingStatus) &&
        (!hierarchyData || (hierarchyData?.sites?.length ?? 0) === 0 && (hierarchyData?.subOrganizations?.length ?? 0) === 0)
    ) {
        return (
            <div className='hierarchy-view--empty'>
                {translate('ABB.Powertrain.Frontend.conditionIndicesOverviewNoItems')}
            </div>
        );
    }

    return hierarchyData && Object.keys(hierarchyData).length > 0 &&
        <div className={`hierarchy-list ${showFilter ? 'show-filter' : 'without-filter'}`}
            onScrollCapture={handleHierarchyScroll}
            onWheelCapture={handleHierarchyScroll}
            onTouchMoveCapture={handleHierarchyScroll}
        >
            {renderHeader()}
            <HierarchyExpansionContext.Provider value={expansionContextValue}>
                <div className='hierarchy-view'
                    ref={hierarchyViewRef}
                    onScrollCapture={handleHierarchyScroll}
                    onWheelCapture={handleHierarchyScroll}
                    onTouchMoveCapture={handleHierarchyScroll}
                >
                    <OrganizationNode
                        key={hierarchyData.organizationId}
                        organization={hierarchyData}
                        depth={0}
                        selectedAsset={selectedAsset}
                        onSelectAsset={handleSelectAsset}
                        leftPanelSize={leftPanelSize}
                        allAssets={allAssets}
                        conditionIndicesView={conditionIndicesView}
                        selectionNodeIds={selectionNodeIds}
                        selectedAssetSignalCounts={selectedAssetSignalCounts}
                    />
                    <div className='hierarchy-view__bottom-space'>
                        {translate('ABB.Powertrain.Frontend.conditionOverviewConfigurePowertrainsLabel')} <Link className='hierarchy-view__bottom-space__link' to={{ pathname: routes.ToolsPowertrainConfigurationV2 }}>{translate('ABB.Powertrain.Frontend.conditionOverviewConfigurePowertrainsLinkLabel')}</Link>
                    </div>
                </div>
            </HierarchyExpansionContext.Provider>
        </div>;
});

HierarchyViewComponent.propTypes = {
    selectedAsset: PropTypes.object,
    onSelectAsset: PropTypes.func,
    leftPanelSize: PropTypes.number,
    allAssets: PropTypes.array,
    hierarchyData: PropTypes.shape({
        sites: PropTypes.array,
        subOrganizations: PropTypes.array,
    }),
    conditionIndicesView: PropTypes.string,
    showFilter: PropTypes.bool,
    hierarchyLoadingStatus: PropTypes.string,
    expandCollapseCommand: PropTypes.shape({
        mode: PropTypes.string,
        nonce: PropTypes.number
    }),
    selectedAssetIds: PropTypes.array,
    selectedAssetSignalCounts: PropTypes.object
};

HierarchyViewComponent.displayName = 'HierarchyViewComponent';

export default HierarchyViewComponent;
